import type { CSSProperties } from "react";

/**
 * Global theme typography tokens (Theme Manager — not page-builder documents).
 * Stored under `ThemeSiteDesign.typography.elements` in the tenant DB.
 */

export type TypographyElementKey =
	| "h1"
	| "h2"
	| "h3"
	| "h4"
	| "h5"
	| "h6"
	| "p"
	| "span"
	| "blockquote"
	| "a"
	| "ul"
	| "pre";

export type TypographyElementStyle = {
	fontFamily?: string;
	fontSize?: string;
	fontWeight?: string;
	lineHeight?: string;
	letterSpacing?: string;
	color?: string;
	/** Dark-mode override for `color` (rendered under `.dark` selector). */
	colorDark?: string;
	fontStyle?: string;
	/** Persisted type-level props (parity with Text/Heading Main Options). */
	textDecoration?: string;
	textTransform?: string;
	textAlign?: string;
	/**
	 * Default block spacing above/below the tag (Text/Heading “Line spacing”).
	 * Emitted as CSS `padding-top` / `padding-bottom` on semantic tag selectors.
	 */
	paddingTop?: string;
	paddingBottom?: string;
	/**
	 * Optional gradient text fill (emitted on semantic tag selectors).
	 * Safe for theme CSS: clip/fill only apply when `backgroundImage` is set.
	 */
	backgroundImage?: string;
	backgroundClip?: string;
	WebkitBackgroundClip?: string;
	WebkitTextFillColor?: string;
};

/** Matches page-builder toolbar breakpoints. */
export type TypographyBreakpointKey = "desktop" | "tablet" | "mobile";

export const TYPOGRAPHY_BREAKPOINT_ORDER: readonly TypographyBreakpointKey[] = [
	"desktop",
	"tablet",
	"mobile",
] as const;

/** Per-element styles per breakpoint (stored shape may be flat legacy or this object). */
export type TypographyElementBreakpoints = {
	desktop?: TypographyElementStyle;
	tablet?: TypographyElementStyle;
	mobile?: TypographyElementStyle;
};

/** Resolved full map per viewport for the typography builder. */
export type TypographyStylesByViewport = Record<
	TypographyBreakpointKey,
	Record<TypographyElementKey, TypographyElementStyle>
>;

export type ThemeTypographyEditorValue = {
	fontSans?: string;
	fontSerif?: string;
	fontMono?: string;
	baseFontSize?: string;
	elements?: Partial<
		Record<string, TypographyElementStyle | TypographyElementBreakpoints>
	>;
};

/** Keys shown in the builder preview (order). */
export const TYPOGRAPHY_ELEMENT_ORDER: readonly TypographyElementKey[] = [
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"p",
	"span",
	"blockquote",
	"a",
	"ul",
	"pre",
] as const;

export const TYPOGRAPHY_ELEMENT_LABELS: Record<TypographyElementKey, string> = {
	h1: "H1",
	h2: "H2",
	h3: "H3",
	h4: "H4",
	h5: "H5",
	h6: "H6",
	p: "Paragraph",
	span: "Span",
	blockquote: "Blockquote",
	a: "Links",
	ul: "Lists",
	pre: "Preformatted",
};

/**
 * Mega / replica chrome roots so Theme Manager tag rules (`a`, `h*`, …) with
 * `!important` do not crush header link colors. Selectors Level 4 `:not()` —
 * the element must not be a descendant of these roots.
 *
 * - `data-pb-mega-menu` — classic / bubble mega header
 * - `data-pb-advanced-mega-menu` — replica group header (canvas + public)
 * - `data-ghh-flyout` — replica mega panel if it is not nested in the header
 */
export const MEGA_MENU_TYPOGRAPHY_EXCLUDE =
	":not([data-pb-mega-menu] *):not([data-pb-advanced-mega-menu] *):not([data-ghh-flyout] *)";

/**
 * Custom HTML / React custom_code roots use `data-pb-custom-code-root` and bring
 * their own CSS (including CTA buttons). Theme Manager tag rules must not win.
 */
export const CUSTOM_CODE_TYPOGRAPHY_EXCLUDE =
	":not([data-pb-custom-code-root] *)";

/**
 * Hands a `container.ownTypography` subtree to page/site CSS, which otherwise
 * cannot beat theme `!important` at ~(0,3,1). `:where()` keeps the exclude at
 * zero specificity — a bare `:not()` would lift every theme rule by (0,1,0) and
 * silently re-decide contests on live pages. Do not retrofit the two excludes
 * above; guarded by `typography-specificity.test.ts`.
 */
export const OWN_TYPOGRAPHY_EXCLUDE = ":not(:where([data-pb-own-type] *))";

/** Excludes for author element overrides — no `OWN_TYPOGRAPHY_EXCLUDE`, or opting a section out would disable them too. */
export const TYPOGRAPHY_SCOPED_EXCLUDES = `${MEGA_MENU_TYPOGRAPHY_EXCLUDE}${CUSTOM_CODE_TYPOGRAPHY_EXCLUDE}`;

/** Excludes for Theme Manager *tag* rules only — these yield to an opted-out section. */
export const THEME_TAG_TYPOGRAPHY_EXCLUDES = `${TYPOGRAPHY_SCOPED_EXCLUDES}${OWN_TYPOGRAPHY_EXCLUDE}`;

/**
 * `entry_meta` author byline uses a real `<a>` for navigation but must not pick
 * up Theme Manager link color / underline (reads as body text, not a hyperlink).
 */
export const ENTRY_META_AUTHOR_LINK_CLASS = "pb-entry-meta-author";

/**
 * Native builder `button` elements often render as `<a href>`; exclude them from
 * Theme Manager link typography so button styles control color / decoration.
 */
export const BUILDER_BUTTON_LINK_CLASS = "pb-builder-button";

/**
 * Archive chrome (filter/sort chips, pagination) are `<a>`s styled as controls —
 * exclude them from Theme Manager link typography so the chip tokens
 * (primary / primary-fg) control color, not the site's hyperlink color.
 */
export const ARCHIVE_CHROME_LINK_CLASS = "pb-archive-chrome";

/**
 * Form field / consent label links (`FormLabelWithLinks`). Theme Manager `a`
 * color is often authored for dark chrome and would wash out consent copy on
 * white form cards — exclude so form design / labelStyles own the color.
 */
export const FORM_LABEL_LINK_CLASS = "pb-form-label-link";

/** CSS selectors for Theme Manager typography rules (canvas + public site). */
export function typographyElementSelector(key: TypographyElementKey): string {
	const linkSkip = `:not(.${ENTRY_META_AUTHOR_LINK_CLASS}):not(.${BUILDER_BUTTON_LINK_CLASS}):not(.${FORM_LABEL_LINK_CLASS}):not(.${ARCHIVE_CHROME_LINK_CLASS})`;
	const base =
		key === "ul"
			? "ul, ol"
			: key === "a"
				? `a${linkSkip}, a.interlink${linkSkip}, span.interlink-missing`
				: key;
	return base
		.split(",")
		.map((part) => `${part.trim()}${THEME_TAG_TYPOGRAPHY_EXCLUDES}`)
		.join(", ");
}

/**
 * Legacy tag default stack (seeded in older tenants). Remapped to
 * {@link SITE_SANS_FONT_FAMILY_WITH_FALLBACK} when emitting Theme CSS so Sans
 * from Theme Manager wins without requiring a re-save.
 */
export const LEGACY_DEFAULT_SERIF_STACK =
	'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif';

/** Site-wide Sans token — tag defaults and chrome should follow Theme Manager Sans. */
export const DEFAULT_SITE_FONT_FAMILY = "var(--tt-font-sans)";

/** Baseline styles for preview before any custom values. */
export const DEFAULT_TYPOGRAPHY_ELEMENTS: Record<
	TypographyElementKey,
	TypographyElementStyle
> = {
	h1: {
		fontFamily: DEFAULT_SITE_FONT_FAMILY,
		fontSize: "2.25rem",
		fontWeight: "700",
		lineHeight: "1.2",
		color: "#0f172a",
	},
	h2: {
		fontFamily: DEFAULT_SITE_FONT_FAMILY,
		fontSize: "1.875rem",
		fontWeight: "700",
		lineHeight: "1.25",
		color: "#0f172a",
	},
	h3: {
		fontFamily: DEFAULT_SITE_FONT_FAMILY,
		fontSize: "1.5rem",
		fontWeight: "600",
		lineHeight: "1.3",
		color: "#0f172a",
	},
	h4: {
		fontFamily: DEFAULT_SITE_FONT_FAMILY,
		fontSize: "1.25rem",
		fontWeight: "600",
		lineHeight: "1.35",
		color: "#0f172a",
	},
	h5: {
		fontFamily: DEFAULT_SITE_FONT_FAMILY,
		fontSize: "1.125rem",
		fontWeight: "600",
		lineHeight: "1.4",
		color: "#0f172a",
	},
	h6: {
		fontFamily: DEFAULT_SITE_FONT_FAMILY,
		fontSize: "1rem",
		fontWeight: "600",
		lineHeight: "1.4",
		color: "#334155",
	},
	p: {
		fontFamily: DEFAULT_SITE_FONT_FAMILY,
		fontSize: "1rem",
		fontWeight: "400",
		lineHeight: "1.6",
		color: "#0f172a",
	},
	/**
	 * Inline `<span>` in custom HTML / rich text. Default only sets family so
	 * size/weight/color inherit from the parent (heading, paragraph, etc.).
	 * Theme Manager can still set size/color explicitly when needed.
	 */
	span: {
		fontFamily: DEFAULT_SITE_FONT_FAMILY,
	},
	blockquote: {
		fontFamily: DEFAULT_SITE_FONT_FAMILY,
		fontSize: "1rem",
		fontWeight: "400",
		fontStyle: "italic",
		lineHeight: "1.6",
		color: "#334155",
	},
	a: {
		fontFamily: DEFAULT_SITE_FONT_FAMILY,
		fontSize: "1rem",
		fontWeight: "400",
		lineHeight: "1.6",
		color: "#b91c1c",
	},
	ul: {
		fontFamily: DEFAULT_SITE_FONT_FAMILY,
		fontSize: "1rem",
		fontWeight: "400",
		lineHeight: "1.6",
		color: "#0f172a",
	},
	pre: {
		fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
		fontSize: "0.875rem",
		fontWeight: "400",
		lineHeight: "1.5",
		color: "#0f172a",
	},
};

/** True when `fontFamily` is the old seed stack (Times/Georgia), not an intentional custom serif. */
export function isLegacyDefaultSerifFontFamily(value: string): boolean {
	const normalized = value.trim().replace(/\s+/g, " ");
	const legacy = LEGACY_DEFAULT_SERIF_STACK.replace(/\s+/g, " ");
	if (
		normalized === legacy ||
		normalized === "ui-serif, Georgia, Cambria, Times New Roman, Times, serif"
	) {
		return true;
	}
	// Quote / spacing variants of the same Theme Manager seed stack.
	const hasGeorgia = /georgia/i.test(normalized);
	const hasCambria = /cambria/i.test(normalized);
	const hasTimes = /times new roman|\btimes\b/i.test(normalized);
	const hasUiSerif = /ui-serif/i.test(normalized);
	if ((hasUiSerif || (hasGeorgia && hasCambria)) && hasTimes) {
		return true;
	}
	return false;
}

/**
 * Concrete UA-safe Sans stack. Safe to assign to `--tt-font-sans` itself.
 * Never wrap this in `var(--tt-font-sans, …)` when defining that property —
 * that creates a cyclic custom property and browsers fall back to system-ui.
 */
export const SITE_SANS_FALLBACK_STACK =
	'system-ui, -apple-system, "Segoe UI", sans-serif';

/**
 * Site Sans with UA-safe fallbacks. Use in *consumers* of `--tt-font-sans`
 * (not when defining the custom property) so an unset variable cannot fall
 * through to Times New Roman.
 */
export const SITE_SANS_FONT_FAMILY_WITH_FALLBACK = `var(--tt-font-sans, ${SITE_SANS_FALLBACK_STACK})`;

/** True when `fontFamily` is the site Sans CSS variable (with or without fallbacks). */
export function isSiteSansFontFamilyToken(value: string): boolean {
	const trimmed = value.trim();
	return (
		trimmed === DEFAULT_SITE_FONT_FAMILY ||
		trimmed === SITE_SANS_FONT_FAMILY_WITH_FALLBACK ||
		/^var\(\s*--tt-font-sans\b/i.test(trimmed)
	);
}

/**
 * Concrete font stack saved on a Theme Manager element (flat or breakpoint shape).
 * Ignores site-Sans tokens and legacy Times seeds.
 */
export function inferConcreteFontSansFromElements(
	savedElements: unknown
): string | null {
	if (!isPlainObject(savedElements)) {
		return null;
	}
	for (const key of ["p", "span", "h1"] as const) {
		const bp = parseTypographyElementToBreakpoints(savedElements[key]);
		const candidate = bp.desktop?.fontFamily?.trim();
		if (
			candidate &&
			!isSiteSansFontFamilyToken(candidate) &&
			!isLegacyDefaultSerifFontFamily(candidate)
		) {
			return candidate;
		}
	}
	return null;
}

/**
 * Theme Manager Sans for CSS emission (variables + `font-family` pins).
 * Returns a **concrete** stack only — never `var(--tt-font-sans, …)` — so
 * assigning the result to `--tt-font-sans` cannot become cyclic.
 *
 * Order: explicit `fontSans` → concrete family on paragraph/span/h1 → UA stack.
 */
export function resolveSiteSansFontFamilyForCss(
	fontSans: string | null | undefined,
	savedElements?: unknown
): string {
	if (typeof fontSans === "string" && fontSans.trim().length > 0) {
		const trimmed = fontSans.trim();
		if (
			!(
				isLegacyDefaultSerifFontFamily(trimmed) ||
				isSiteSansFontFamilyToken(trimmed)
			)
		) {
			return trimmed;
		}
	}
	const inferred = inferConcreteFontSansFromElements(savedElements);
	if (inferred) {
		return inferred;
	}
	return SITE_SANS_FALLBACK_STACK;
}

/** CSS generic font-family keywords (always resolve — must not precede Theme Sans). */
const CSS_GENERIC_FONT_FAMILIES = new Set([
	"serif",
	"sans-serif",
	"monospace",
	"cursive",
	"fantasy",
	"system-ui",
	"ui-serif",
	"ui-sans-serif",
	"ui-monospace",
	"ui-rounded",
	"emoji",
	"math",
	"fangsong",
]);

/** Split a CSS `font-family` list on commas outside quotes. */
export function splitCssFontFamilyList(value: string): string[] {
	const parts: string[] = [];
	let current = "";
	let quote: '"' | "'" | null = null;
	for (let i = 0; i < value.length; i++) {
		const ch = value[i];
		if (quote) {
			current += ch;
			if (ch === quote && value[i - 1] !== "\\") {
				quote = null;
			}
			continue;
		}
		if (ch === '"' || ch === "'") {
			quote = ch;
			current += ch;
			continue;
		}
		if (ch === ",") {
			if (current.trim()) {
				parts.push(current.trim());
			}
			current = "";
			continue;
		}
		current += ch;
	}
	if (current.trim()) {
		parts.push(current.trim());
	}
	return parts;
}

function isCssGenericFontFamily(part: string): boolean {
	const bare = part
		.replace(/^["']|["']$/g, "")
		.trim()
		.toLowerCase();
	return CSS_GENERIC_FONT_FAMILIES.has(bare);
}

/**
 * Ensure a font stack falls back to Theme Manager global Sans (`--tt-font-sans`)
 * before UA generics. Named faces stay first; trailing `system-ui` / `sans-serif`
 * etc. are replaced by {@link SITE_SANS_FONT_FAMILY_WITH_FALLBACK}.
 */
export function withGlobalTypographyFontFallback(fontFamily: string): string {
	const trimmed = fontFamily.trim();
	if (!trimmed) {
		return trimmed;
	}
	if (
		isLegacyDefaultSerifFontFamily(trimmed) ||
		isSiteSansFontFamilyToken(trimmed)
	) {
		return SITE_SANS_FONT_FAMILY_WITH_FALLBACK;
	}
	if (/--tt-font-sans\b/i.test(trimmed)) {
		return trimmed;
	}
	const parts = splitCssFontFamilyList(trimmed);
	const named: string[] = [];
	for (const part of parts) {
		if (isCssGenericFontFamily(part)) {
			break;
		}
		named.push(part);
	}
	if (named.length === 0) {
		return SITE_SANS_FONT_FAMILY_WITH_FALLBACK;
	}
	return `${named.join(", ")}, ${SITE_SANS_FONT_FAMILY_WITH_FALLBACK}`;
}

/**
 * Map legacy tag default serif → site Sans (with fallbacks) for CSS emission.
 * Custom stacks keep their named faces, then fall back to Theme Manager global
 * Sans. Bare `var(--tt-font-sans)` is upgraded with a UA-safe fallback list.
 */
export function resolveTypographyFontFamilyForCss(value: string): string {
	return withGlobalTypographyFontFallback(value);
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
	return typeof v === "object" && v !== null && !Array.isArray(v);
}

const TYPO_STYLE_KEYS: (keyof TypographyElementStyle)[] = [
	"fontFamily",
	"fontSize",
	"fontWeight",
	"lineHeight",
	"letterSpacing",
	"color",
	"colorDark",
	"fontStyle",
	"textDecoration",
	"textTransform",
	"textAlign",
	"paddingTop",
	"paddingBottom",
	"backgroundImage",
	"backgroundClip",
	"WebkitBackgroundClip",
	"WebkitTextFillColor",
];

function mergeTypographyStyle(
	base: TypographyElementStyle,
	override?: TypographyElementStyle
): TypographyElementStyle {
	if (!override) {
		return { ...base };
	}
	return { ...base, ...override };
}

/** True when `value` uses explicit `desktop` / `tablet` / `mobile` buckets. */
export function isResponsiveTypographyElement(
	value: unknown
): value is TypographyElementBreakpoints {
	if (!isPlainObject(value)) {
		return false;
	}
	return TYPOGRAPHY_BREAKPOINT_ORDER.some(
		(k) => k in value && isPlainObject(value[k])
	);
}

/** Normalize DB / API value to breakpoint buckets (legacy flat → desktop only). */
export function parseTypographyElementToBreakpoints(
	raw: unknown
): TypographyElementBreakpoints {
	if (!isPlainObject(raw)) {
		return {};
	}
	if (isResponsiveTypographyElement(raw)) {
		const out: TypographyElementBreakpoints = {};
		for (const k of TYPOGRAPHY_BREAKPOINT_ORDER) {
			const slice = raw[k];
			if (isPlainObject(slice)) {
				out[k] = slice as TypographyElementStyle;
			}
		}
		return out;
	}
	return { desktop: raw as TypographyElementStyle };
}

/** Resolved style for one element at a viewport (defaults → desktop → tablet → mobile). */
export function resolvedTypographyStyleForViewport(
	key: TypographyElementKey,
	bp: TypographyElementBreakpoints,
	viewport: TypographyBreakpointKey
): TypographyElementStyle {
	const defaults = DEFAULT_TYPOGRAPHY_ELEMENTS[key];
	const d = mergeTypographyStyle(defaults, bp.desktop);
	const t = mergeTypographyStyle(d, bp.tablet);
	const m = mergeTypographyStyle(t, bp.mobile);
	if (viewport === "desktop") {
		return d;
	}
	if (viewport === "tablet") {
		return t;
	}
	return m;
}

/** Full element map for each breakpoint (for builder + save). */
export function buildTypographyStylesByViewport(
	savedElements: unknown
): TypographyStylesByViewport {
	const root = isPlainObject(savedElements) ? savedElements : {};
	const desktop = {} as Record<TypographyElementKey, TypographyElementStyle>;
	const tablet = {} as Record<TypographyElementKey, TypographyElementStyle>;
	const mobile = {} as Record<TypographyElementKey, TypographyElementStyle>;
	for (const key of TYPOGRAPHY_ELEMENT_ORDER) {
		const bp = parseTypographyElementToBreakpoints(root[key]);
		desktop[key] = resolvedTypographyStyleForViewport(key, bp, "desktop");
		tablet[key] = resolvedTypographyStyleForViewport(key, bp, "tablet");
		mobile[key] = resolvedTypographyStyleForViewport(key, bp, "mobile");
	}
	return { desktop, tablet, mobile };
}

export function mergeTypographyElementsForViewport(
	saved: unknown,
	viewport: TypographyBreakpointKey
): Record<TypographyElementKey, TypographyElementStyle> {
	return buildTypographyStylesByViewport(saved)[viewport];
}

/** Merge saved `elements` onto defaults for preview (desktop / largest breakpoint). */
export function mergeTypographyElementsWithDefaults(
	saved: unknown
): Record<TypographyElementKey, TypographyElementStyle> {
	return mergeTypographyElementsForViewport(saved, "desktop");
}

export function typographyStylesEqual(
	a: TypographyElementStyle,
	b: TypographyElementStyle
): boolean {
	for (const k of TYPO_STYLE_KEYS) {
		if (a[k] !== b[k]) {
			return false;
		}
	}
	return true;
}

export function typographyStylesEqualMap(
	a: Record<TypographyElementKey, TypographyElementStyle>,
	b: Record<TypographyElementKey, TypographyElementStyle>
): boolean {
	for (const key of TYPOGRAPHY_ELEMENT_ORDER) {
		if (!typographyStylesEqual(a[key], b[key])) {
			return false;
		}
	}
	return true;
}

/** Persist: flat object when only desktop differs from tablet/mobile; else breakpoint object. */
export function compactTypographyElementsForSave(
	byVp: TypographyStylesByViewport
): NonNullable<ThemeTypographyEditorValue["elements"]> {
	const out: Record<string, unknown> = {};
	for (const key of TYPOGRAPHY_ELEMENT_ORDER) {
		const d = byVp.desktop[key];
		const t = byVp.tablet[key];
		const m = byVp.mobile[key];
		if (typographyStylesEqual(t, d) && typographyStylesEqual(m, d)) {
			out[key] = d;
		} else if (typographyStylesEqual(m, t)) {
			out[key] = { desktop: d, tablet: t };
		} else {
			out[key] = { desktop: d, tablet: t, mobile: m };
		}
	}
	return out as NonNullable<ThemeTypographyEditorValue["elements"]>;
}

/**
 * Map a page-builder HTML tag to a Theme Manager typography element key.
 * Flow tags without a dedicated theme row fall back to `p` (or `span` for
 * inline-ish tags).
 */
export function mapHtmlTagToTypographyElementKey(
	tag: string | undefined
): TypographyElementKey {
	const t = (tag ?? "p").trim().toLowerCase();
	if ((TYPOGRAPHY_ELEMENT_ORDER as readonly string[]).includes(t)) {
		return t as TypographyElementKey;
	}
	if (t === "small") {
		return "span";
	}
	return "p";
}

/**
 * Resolved Theme Manager styles for an element tag at a viewport (defaults →
 * saved desktop → tablet → mobile). Used by Main Options placeholders.
 */
export function resolveTagTypographyStyle(
	tag: string | undefined,
	savedElements: unknown,
	viewport: TypographyBreakpointKey = "desktop"
): TypographyElementStyle {
	const key = mapHtmlTagToTypographyElementKey(tag);
	return mergeTypographyElementsForViewport(savedElements, viewport)[key];
}

/** Parse Theme Manager `baseFontSize` (e.g. `16px`) to a rem root in px. */
export function parseTypographyRemBasePx(
	baseFontSize: string | null | undefined,
	fallback = 16
): number {
	const raw = (baseFontSize ?? "").trim();
	if (!raw) {
		return fallback;
	}
	const px = /^([\d.]+)\s*px$/i.exec(raw);
	if (px) {
		const n = Number.parseFloat(px[1]);
		return Number.isFinite(n) && n > 0 ? n : fallback;
	}
	const n = Number.parseFloat(raw);
	return Number.isFinite(n) && n > 0 ? n : fallback;
}

/** Deep-merge one element node for PATCH (legacy flat or responsive buckets). */
export function mergeTypographyElementStored(
	base: unknown,
	patch: unknown
): Record<string, unknown> {
	const bBp = parseTypographyElementToBreakpoints(base);
	const pBp = parseTypographyElementToBreakpoints(patch);
	return {
		desktop: {
			...(bBp.desktop ?? {}),
			...(pBp.desktop ?? {}),
		} as TypographyElementStyle,
		tablet: {
			...(bBp.tablet ?? {}),
			...(pBp.tablet ?? {}),
		} as TypographyElementStyle,
		mobile: {
			...(bBp.mobile ?? {}),
			...(pBp.mobile ?? {}),
		} as TypographyElementStyle,
	};
}

export function typographyStyleToCss(
	style: TypographyElementStyle
): CSSProperties {
	const css: CSSProperties = {};
	if (style.fontFamily) {
		css.fontFamily = style.fontFamily;
	}
	if (style.fontSize) {
		css.fontSize = style.fontSize;
	}
	if (style.fontWeight) {
		css.fontWeight = style.fontWeight as CSSProperties["fontWeight"];
	}
	if (style.lineHeight) {
		css.lineHeight = style.lineHeight;
	}
	if (style.letterSpacing) {
		css.letterSpacing = style.letterSpacing;
	}
	if (style.color) {
		css.color = style.color;
	}
	if (style.fontStyle) {
		css.fontStyle = style.fontStyle as CSSProperties["fontStyle"];
	}
	if (style.textDecoration) {
		css.textDecoration = style.textDecoration;
	}
	if (style.textTransform) {
		css.textTransform = style.textTransform as CSSProperties["textTransform"];
	}
	if (style.textAlign) {
		css.textAlign = style.textAlign as CSSProperties["textAlign"];
	}
	if (style.backgroundImage) {
		css.backgroundImage = style.backgroundImage;
	}
	if (style.backgroundClip) {
		css.backgroundClip =
			style.backgroundClip as CSSProperties["backgroundClip"];
	}
	if (style.WebkitBackgroundClip) {
		css.WebkitBackgroundClip = style.WebkitBackgroundClip;
	}
	if (style.WebkitTextFillColor) {
		css.WebkitTextFillColor = style.WebkitTextFillColor;
	}
	return css;
}
